import { NgZone } from '@angular/core';
import { HubConnectionBuilder, HubConnection, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Subject, merge, Observable as rxObservable, map, firstValueFrom, distinctUntilChanged } from 'rxjs';

import { decodeQueryString } from 'rev-shared/util/Util.Service';
import { noop } from 'rev-shared/util';
import { SignalRReconnectPolicy } from './SignalRReconnectPolicy';

import { IHubProxy } from './IHubProxy';
import { IObservable, Observable } from './Observable';

interface IHubProxyConfig {
	clientMethods: string[];
	serverMethods: string[];
}

export class SignalRHubsConnection {
	private readonly hubConnection: HubConnection;
	private hubProxy: IHubProxy;
	private pendingInvoke: Promise<any>;

	private readonly startedSubject$: Subject<void> = new Subject<void>();
	public readonly startupPromise: Promise<void> = firstValueFrom(this.startedSubject$);
	public readonly close$: rxObservable<any>;
	public readonly reconnecting$: rxObservable<any>;
	public readonly reconnected$: rxObservable<string>;
	public readonly stateChanged$: rxObservable<HubConnectionState>;
	//Emits whenever the connectionId changes. Sometimes reconnect can resume the same connection.
	//If that fails, a new connection opens with a different connectionId.
	public readonly connectionId$: rxObservable<string>;

	public options: SignalR.ConnectionOptions;
	public started: boolean;
	public starting: boolean;
	public zone: NgZone;

	constructor(
		connectionUrl: string,
		private hubProxyCfg: IHubProxyConfig
	) {
		this.hubConnection = new HubConnectionBuilder()
			.withUrl(connectionUrl, decodeQueryString().transport)//TODO: validate transport before passing in. Test Websockets on android again.
			.withAutomaticReconnect(new SignalRReconnectPolicy())
			.configureLogging(LogLevel.Debug)
			.build();

		this.pendingInvoke = Promise.resolve();
		this.started = false;

		this.close$ = buildObservable(cb => this.hubConnection.onclose(cb));
		this.reconnecting$ = buildObservable(cb => this.hubConnection.onreconnecting(cb));
		this.reconnected$ = buildObservable(cb => this.hubConnection.onreconnected(cb));
		this.stateChanged$ = merge(this.close$, this.reconnecting$, this.reconnected$, this.startedSubject$)
			.pipe(map(() => this.hubConnection.state));

		this.connectionId$ = merge(
			this.startedSubject$,
			this.reconnected$
		).pipe(
			map(() => this.hubConnection.connectionId),
			distinctUntilChanged()
		);

		this.init();
	}

	private init(): void {
		this.hubProxy = this.createHubProxy(this.hubProxyCfg.serverMethods, this.hubProxyCfg.clientMethods);
	}

	private createHubProxy(serverMethods: string[], clientMethods: string[]): IHubProxy {
		const server = serverMethods.reduce((proxy, method) => {
			proxy[method] = (...args: any[]) => {
				if(this.hubConnection.state === HubConnectionState.Connected) {
					return this.hubConnection.invoke(method, ...args);
				}
				console.warn('SignalRHubsConnection: made call before connection started: ', method);

				return firstValueFrom(this.startedSubject$)
					.then(() => this.hubConnection.invoke(method, ...args))
					.finally(() => this.safeApply());
			};
			return proxy;
		}, {});

		const client: IObservable = new Observable();
		clientMethods.forEach(clientMethod => {
			this.hubConnection.on(clientMethod, (...args: any[]) => {
				client.fire(clientMethod, ...args);
				this.safeApply();
			});
		});

		return {
			client,
			server
		};
	}

	private safeApply(): void { // TODO: replace with alternative. Perhaps fire()
		this.zone?.run(noop);
	}

	public get connectionId(): string {
		return this.hubConnection.connectionId;
	}

	public addPendingOperation(p: Promise<any>): void {
		this.pendingInvoke = Promise.all([this.pendingInvoke, p])
			.catch(noop);
	}

	public bounce(): Promise<any> {
		return this.startupPromise
			.then(() => this.stop(true))
			.then(() => this.start());
	}

	public getConnectionStatus(): HubConnectionState {
		return this.hubConnection.state;
	}

	public isConnected(): boolean {
		return this.hubConnection.state === HubConnectionState.Connected;
	}

	public getProxy(): IHubProxy {
		return this.hubProxy;
	}

	/**
	 * Starts the signalR connection. Returns a promise that resolves when the connection is established.
	 * Do not start until all hub proxies have been created, during app startup
	 * options - signalR hubConnection options object.
	 *    additionally:
	 *    reconnectRetryAttempts
	 *    reconnectRetryDelay -  if connection is dropped, attempt to reconnect with a delay between attempts.
	 */
	public start(reconnect?: boolean): Promise<any> {
		console.log('Signalr: hubs connection start()');
		if(!reconnect && this.started ||
			this.starting) {
			console.log('Signalr: already starting or started.');
			return this.startupPromise;
		}


		this.starting = true;
		console.log('Signalr', 'starting');
		return this.hubConnection.start()
			.then(() => {
				console.log('Now connected, connection ID=', this.hubConnection.connectionId);
				this.started = true;
				this.startedSubject$.next();
				this.safeApply();
			})
			.catch(error => {
				this.safeApply();
				return Promise.reject(error);
			})
			.finally(() => this.starting = false);
	}

	public stop(awaitPendingInvokations: boolean): Promise<any> {
		const stopInternal = (): Promise<any> => {
			this.started = false;
			return this.hubConnection.stop();
		};

		if (awaitPendingInvokations) {
			return this.pendingInvoke.then(stopInternal);
		}

		return stopInternal();
	}
}

function buildObservable<T>(api: (cb: (event: T) => void) => void): rxObservable<T> {
	const s = new Subject<T>();
	api((event: T) => s.next(event));
	return s.asObservable();
}
